/**
 * Canonical Support ticket status contract.
 * Stored / API / filter values are Slovak status strings (not i18n labels).
 */

export const SUPPORT_TICKET_STATUS_CREATED = 'Vytvorený';
export const SUPPORT_TICKET_STATUS_IN_PROGRESS = 'V riešení';
export const SUPPORT_TICKET_STATUS_WAITING = 'Čaká sa';
export const SUPPORT_TICKET_STATUS_WAITING_FOR_REPLY = 'Čaká sa na odpoveď';
export const SUPPORT_TICKET_STATUS_ON_HOLD = 'Pozastavený';
export const SUPPORT_TICKET_STATUS_RESOLVED = 'Vyriešený';

export const SUPPORT_TICKET_STATUSES = Object.freeze([
  SUPPORT_TICKET_STATUS_CREATED,
  SUPPORT_TICKET_STATUS_IN_PROGRESS,
  SUPPORT_TICKET_STATUS_WAITING,
  SUPPORT_TICKET_STATUS_WAITING_FOR_REPLY,
  SUPPORT_TICKET_STATUS_ON_HOLD,
  SUPPORT_TICKET_STATUS_RESOLVED,
]);

/** Non-resolved statuses. Navbar count uses status != Vyriešený; Open list must match. */
export const SUPPORT_TICKET_OPEN_STATUSES = Object.freeze(
  SUPPORT_TICKET_STATUSES.filter((s) => s !== SUPPORT_TICKET_STATUS_RESOLVED)
);

export const SUPPORT_TICKET_STATUS_I18N_KEYS = Object.freeze({
  [SUPPORT_TICKET_STATUS_CREATED]: 'SupportTicketList.Status.Vytvoreny',
  [SUPPORT_TICKET_STATUS_IN_PROGRESS]: 'SupportTicketList.Status.VRieseni',
  [SUPPORT_TICKET_STATUS_WAITING]: 'SupportTicketList.Status.CakaSa',
  [SUPPORT_TICKET_STATUS_WAITING_FOR_REPLY]: 'SupportTicketList.Status.CakaSaNaOdpoved',
  [SUPPORT_TICKET_STATUS_ON_HOLD]: 'SupportTicketList.Status.Pozastaveny',
  [SUPPORT_TICKET_STATUS_RESOLVED]: 'SupportTicketList.Status.Vyrieseny',
});

export const SUPPORT_TICKET_SCOPE_OPEN = 'Otvorené';
export const SUPPORT_TICKET_SCOPE_CLOSED = 'Zatvorené';
export const SUPPORT_TICKET_SCOPE_ALL = 'Všetky';

const OPEN_SCOPE_ALIASES = new Set(['Otvorené', 'Otvorený']);
const CLOSED_SCOPE_ALIASES = new Set(['Zatvorené', 'Uzavretý']);
const ALL_SCOPE_ALIASES = new Set(['Všetky', 'All']);

const KNOWN_STATUS_SET = new Set(SUPPORT_TICKET_STATUSES);

function warnUnknownSupportTicketStatus(raw, normalized) {
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn('[SupportTicket] unknown status', { stored: raw, normalized });
  }
}

export function normalizeSupportTicketStatus(value) {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  return s;
}

export function isKnownSupportTicketStatus(value) {
  return KNOWN_STATUS_SET.has(normalizeSupportTicketStatus(value));
}

const CLOSED_TICKET_STATUSES = new Set([
  SUPPORT_TICKET_STATUS_RESOLVED,
  'Stornovaný',
  'Stornované',
  'Zatvorené',
  'Uzavretý',
]);

export function isClosedSupportTicketStatus(value) {
  return CLOSED_TICKET_STATUSES.has(normalizeSupportTicketStatus(value));
}

export function isOpenSupportTicketStatus(value) {
  const n = normalizeSupportTicketStatus(value);
  if (!n) return true;
  return !isClosedSupportTicketStatus(n);
}

export function isSupportTicketScopeValue(value) {
  const n = normalizeSupportTicketStatus(value);
  return OPEN_SCOPE_ALIASES.has(n) || CLOSED_SCOPE_ALIASES.has(n) || ALL_SCOPE_ALIASES.has(n);
}

export function normalizeSupportTicketListScope(value) {
  const n = normalizeSupportTicketStatus(value);
  if (CLOSED_SCOPE_ALIASES.has(n)) return SUPPORT_TICKET_SCOPE_CLOSED;
  if (ALL_SCOPE_ALIASES.has(n)) return SUPPORT_TICKET_SCOPE_ALL;
  return SUPPORT_TICKET_SCOPE_OPEN;
}

export function isOpenSupportTicketScope(value) {
  return normalizeSupportTicketListScope(value) === SUPPORT_TICKET_SCOPE_OPEN;
}

export function isClosedSupportTicketScope(value) {
  return normalizeSupportTicketListScope(value) === SUPPORT_TICKET_SCOPE_CLOSED;
}

/**
 * Multi-select header values are CSV (primeMultiSelect joins with comma).
 * Must not split on whitespace — statuses contain spaces ("Čaká sa na odpoveď").
 */
export function parseSupportTicketStatusHeaderValues(headerValue) {
  if (headerValue == null || headerValue === '') return [];
  if (Array.isArray(headerValue)) {
    return headerValue.map((x) => normalizeSupportTicketStatus(x)).filter(Boolean);
  }
  if (typeof headerValue === 'object' && Array.isArray(headerValue?.value)) {
    return headerValue.value.map((x) => normalizeSupportTicketStatus(x)).filter(Boolean);
  }
  return String(headerValue)
    .split(',')
    .map((x) => normalizeSupportTicketStatus(x))
    .filter(Boolean)
    .filter((x) => x !== 'null' && x !== 'undefined');
}

export function isAllKnownSupportTicketStatuses(values) {
  const set = new Set((values || []).map(normalizeSupportTicketStatus).filter(Boolean));
  if (set.size !== SUPPORT_TICKET_STATUSES.length) return false;
  return SUPPORT_TICKET_STATUSES.every((s) => set.has(s));
}

export function formatSupportTicketStatusLabel(status, t) {
  const n = normalizeSupportTicketStatus(status);
  const key = SUPPORT_TICKET_STATUS_I18N_KEYS[n];
  if (key && typeof t === 'function') return t(key);
  if (n && !isKnownSupportTicketStatus(n)) {
    warnUnknownSupportTicketStatus(status, n);
  }
  return n;
}

export function buildSupportTicketStatusFilterOptions(t) {
  return SUPPORT_TICKET_STATUSES.map((value) => ({
    label: formatSupportTicketStatusLabel(value, t),
    value,
  }));
}

/**
 * Column exact statuses win over the Open/Closed scope.
 * Open sends `Otvorené` so the backend paginates only non-resolved tickets
 * (including `Čaká sa na odpoveď` and `Pozastavený`).
 */
export function applySupportTicketListScopeStatusParam(extra, scopeStatus) {
  if (!extra || typeof extra !== 'object') return extra;
  const raw = extra.filter_status;
  const columnVals = parseSupportTicketStatusHeaderValues(raw).filter((v) => !isSupportTicketScopeValue(v));
  const usableColumn = columnVals.length > 0 && !isAllKnownSupportTicketStatuses(columnVals);

  if (usableColumn) {
    extra.filter_status = columnVals.length === 1 ? columnVals[0] : columnVals.join(',');
    return extra;
  }

  const scope = normalizeSupportTicketListScope(scopeStatus);
  if (scope === SUPPORT_TICKET_SCOPE_CLOSED) {
    extra.filter_status = SUPPORT_TICKET_SCOPE_CLOSED;
    return extra;
  }
  if (scope === SUPPORT_TICKET_SCOPE_ALL) {
    delete extra.filter_status;
    return extra;
  }
  extra.filter_status = SUPPORT_TICKET_SCOPE_OPEN;
  return extra;
}

export function ticketMatchesSupportListFilter(data, f = {}) {
  if (!data) return false;
  const headerStatusValues = parseSupportTicketStatusHeaderValues(f.headerStatusValues ?? f.headerStatus)
    .filter((v) => !isSupportTicketScopeValue(v));
  const rowStatus = normalizeSupportTicketStatus(data.status);
  const rowUnknown = Boolean(rowStatus) && !isKnownSupportTicketStatus(rowStatus);

  if (headerStatusValues.length > 0 && !isAllKnownSupportTicketStatuses(headerStatusValues)) {
    if (rowUnknown || !rowStatus) return true;
    if (!headerStatusValues.includes(rowStatus)) return false;
  } else if (isClosedSupportTicketScope(f.statusValue)) {
    if (!isClosedSupportTicketStatus(rowStatus)) return false;
  } else if (isOpenSupportTicketScope(f.statusValue) || f.statusValue == null || f.statusValue === '') {
    if (isClosedSupportTicketStatus(rowStatus)) return false;
  }

  if (f.contractorValue && f.contractorValue !== 'Všetky') {
    if (f.contractorValue === 'Mnou vytvorené' && (data.contractor_id == null || String(data.contractor_id) !== String(f.user?.id))) {
      return false;
    }
    if (f.contractorValue === 'Spomenutý') {
      const mentions = data.mentions || [];
      const isMentioned = mentions.some((m) => String(m?.id ?? m?.user_id) === String(f.user?.id));
      if (!isMentioned) return false;
    }
    if (f.contractorValue === 'Vykonávateľ') {
      const uid = String(f.user?.id);
      const inAwaitingId = data.awaiting_user_id != null && String(data.awaiting_user_id) === uid;
      const awaiting = data.awaiting_user;
      const inAwaitingList = Array.isArray(awaiting)
        ? awaiting.some((u) => String(u?.id ?? u?.user_id) === uid)
        : (awaiting && String(awaiting?.id ?? awaiting?.user_id) === uid);
      if (!inAwaitingId && !inAwaitingList) return false;
    }
  }
  if (f.typeValue && f.typeValue !== 'All' && String(data.type) !== String(f.typeValue)) return false;
  return true;
}

/**
 * Client-side row matcher used after a full backend page is already loaded
 * (Pusher / detail-return). Must not be used in Tabulator `ajaxResponse`:
 * shrinking `data` without correcting `total`/`last_page` leaves progressive
 * scroll believing more pages exist while the tableholder is short, which
 * auto-loads page 2..N. Open scope pagination belongs on the backend.
 */
export function filterSupportTicketPagedAjaxResponse(response, f = {}) {
  if (!response || typeof response !== 'object') return response;
  const rows = Array.isArray(response.data) ? response.data : [];
  const data = rows.filter((row) => ticketMatchesSupportListFilter(row, {
    statusValue: f.statusValue,
    headerStatusValues: f.headerStatusValues,
    contractorValue: 'Všetky',
    typeValue: 'All',
    user: null,
  }));
  if (data.length === rows.length) return response;
  return { ...response, data };
}

export function headerFilterMatchesSupportTicketStatus(headerValue, rowValue) {
  if (headerValue == null || headerValue === '') return true;
  const selected = parseSupportTicketStatusHeaderValues(headerValue);
  if (selected.length === 0 || isAllKnownSupportTicketStatuses(selected)) return true;
  const row = normalizeSupportTicketStatus(rowValue);
  if (!row || !isKnownSupportTicketStatus(row)) return true;
  return selected.includes(row);
}
